import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../services/firebase/config';
import URLDebugger from '../components/URLDebugger';
import '../styles/Auth.css';

const ResetPassword: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [codeValid, setCodeValid] = useState(false);
  const [checkingCode, setCheckingCode] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();


  // Extract mode and oobCode from query params
  const params = new URLSearchParams(location.search);
  const oobCode = params.get('oobCode') || '';
  const mode = params.get('mode') || '';

  React.useEffect(() => {
    // Check if this is actually an email verification link that came to the wrong URL
    if (mode === 'verifyEmail' && oobCode) {
      console.log('Email verification link detected on reset password page, redirecting...');
      // This is an email verification link that came to the wrong URL, redirect to verify-email
      let verifyUrl = `/verify-email?mode=${mode}&oobCode=${oobCode}`;
      const params = new URLSearchParams(location.search);
      if (params.get('continueUrl')) {
        verifyUrl += `&continueUrl=${encodeURIComponent(params.get('continueUrl')!)}`;
      }
      if (params.get('lang')) {
        verifyUrl += `&lang=${params.get('lang')}`;
      }
      navigate(verifyUrl, { replace: true });
      return;
    }
    
    // Only check code if mode is resetPassword and oobCode exists
    if (mode === 'resetPassword' && oobCode) {
      setCheckingCode(true);
      verifyPasswordResetCode(auth, oobCode)
        .then(() => {
          setCodeValid(true);
          setCheckingCode(false);
        })
        .catch((err) => {
          setError('This password reset link is invalid or has expired.');
          setCodeValid(false);
          setCheckingCode(false);
        });
    } else if (!mode || !oobCode) {
      setError('Invalid password reset link.');
      setCodeValid(false);
      setCheckingCode(false);
    } else {
      // Unknown mode
      setError(`Invalid link type: ${mode}. Please use the correct link for password reset.`);
      setCodeValid(false);
      setCheckingCode(false);
    }
    // eslint-disable-next-line
  }, [mode, oobCode, navigate, location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!password || !confirmPassword) {
      setError('Please fill in both password fields.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setSuccess('Your password has been reset. You can now log in.');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err: any) {
      setError('Failed to reset password: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-card">
      <URLDebugger />
      <h2>Reset Password</h2>
      {checkingCode ? (
        <div>Checking reset link...</div>
      ) : error ? (
        <div className="auth-error">{error}</div>
      ) : success ? (
        <div className="auth-success">{success}</div>
      ) : codeValid ? (
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
            />
          </div>
          <button className="auth-button" type="submit" disabled={loading}>
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      ) : null}
    </div>
  );
};

export default ResetPassword;
